import "./CurrencyConverter.css";
import {
  convertCurrency,
  resolveCurrencyQuery,
  supportedCurrencies,
} from "./currency/CurrencyApi";
import {
  escapeCurrencyHtml as esc,
  formatCurrencyNumber,
  formatProviderTime,
  type CurrencyResult,
  type SupportedCurrency,
} from "./currency/CurrencyTypes";

const ISO_CODES = new Set(
  "AED AFN ALL AMD ANG AOA ARS AUD AWG AZN BAM BBD BDT BGN BHD BIF BMD BND BOB BRL BSD BTN BWP BYN BZD CAD CDF CHF CLF CLP CNH CNY COP CRC CUP CVE CZK DJF DKK DOP DZD EGP ERN ETB EUR FJD FKP FOK GBP GEL GGP GHS GIP GMD GNF GTQ GYD HKD HNL HRK HTG HUF IDR ILS IMP INR IQD IRR ISK JEP JMD JOD JPY KES KGS KHR KID KMF KRW KWD KYD KZT LAK LBP LKR LRD LSL LYD MAD MDL MGA MKD MMK MNT MOP MRU MUR MVR MWK MXN MYR MZN NAD NGN NIO NOK NPR NZD OMR PAB PEN PGK PHP PKR PLN PYG QAR RON RSD RUB RWF SAR SBD SCR SDG SEK SGD SHP SLE SOS SRD SSP STN SYP SZL THB TJS TMT TND TOP TRY TTD TVD TWD TZS UAH UGX USD UYU UZS VES VND VUV WST XAF XCD XDR XOF XPF YER ZAR ZMW ZWL"
    .split(" "),
);

const NAME_HINT =
  /\b(?:naira|nigerian\s+naira|dollars?|us\s+dollars?|euro|euros|pounds?|sterling|yuan|renminbi|china\s+currency|chinese\s+currency|yen|cedi|rand|rupees?|dirham|riyal|franc|shilling|currency|exchange\s+rate|forex)\b/i;

export function looksLikeCurrencyConversionQuery(query: string): boolean {
  const q = String(query || "").trim();
  if (!q) return false;

  const codes = q.match(/\b[A-Za-z]{3}\b/g) || [];
  const knownCodes = codes.filter((code) => ISO_CODES.has(code.toUpperCase()));

  if (knownCodes.length >= 2) return true;
  if (knownCodes.length >= 1 && /\b(?:to|into|in|rate|price|currency|worth|equals?|convert)\b/i.test(q)) return true;

  return (
    NAME_HINT.test(q) &&
    /\b(?:to|into|in|worth|equals?|convert(?:ed)?|rate|price|today|current(?:ly)?)\b/i.test(q)
  );
}

export function looksLikeExplicitCurrencyAmount(query: string): boolean {
  return /(?:^|\s|[$€£₦₹])\d+(?:[.,]\d+)*(?:\s*(?:thousand|million|billion|trillion|quadrillion))?/i.test(
    String(query || ""),
  );
}

function options(currencies: SupportedCurrency[], selected: string): string {
  return currencies
    .map(
      (item) =>
        `<option value="${esc(item.code)}" ${item.code === selected ? "selected" : ""}>${esc(item.name)} (${esc(item.code)})</option>`,
    )
    .join("");
}

function sourceHtml(data: CurrencyResult): string {
  const source = data.source;
  const label = source?.title || source?.name || "Exchange-rate source";
  const updated = formatProviderTime(data.provider_updated_at);
  return `
    <div class="currency-card-source">
      <span>${updated ? `Updated ${esc(updated)}` : "Latest cached rate"}</span>
      ${
        source?.url
          ? `<a href="${esc(source.url)}" target="_blank" rel="noopener noreferrer">${esc(label)}</a>`
          : `<span>${esc(label)}</span>`
      }
    </div>
  `;
}

function resultMarkup(data: CurrencyResult): string {
  return `
    <div class="currency-converter-result" data-currency-result>
      <div class="currency-converter-equation">
        <span>${esc(data.display_input)}</span>
        <span class="currency-converter-equals">=</span>
        <strong>${esc(data.display_result)}</strong>
      </div>

      <div class="currency-converter-rate">
        1 ${esc(data.from.code)} ≈ ${esc(formatCurrencyNumber(data.rate))} ${esc(data.to.code)}
      </div>

      ${
        data.ngn_reference
          ? `
            <div class="currency-converter-ngn">
              <span>Nigerian Naira reference</span>
              <strong>${esc(data.ngn_reference.display)}</strong>
            </div>
          `
          : ""
      }

      <p class="currency-card-disclaimer">
        Indicative exchange rate. Banks, cards and transfer services may apply spreads or fees.
      </p>
      ${sourceHtml(data)}
    </div>
  `;
}


async function currencyListFor(data: CurrencyResult): Promise<SupportedCurrency[]> {
  const currencies = await supportedCurrencies().catch(() => []);
  return currencies.length > 0
    ? currencies
    : [
        { code: data.from.code, name: data.from.name },
        { code: data.to.code, name: data.to.name },
      ];
}

function attachCurrencyConverterEvents(
  container: HTMLElement,
): void {
  const amount = container.querySelector<HTMLInputElement>("[data-currency-amount]");
  const from = container.querySelector<HTMLSelectElement>("[data-currency-from]");
  const to = container.querySelector<HTMLSelectElement>("[data-currency-to]");
  const swap = container.querySelector<HTMLButtonElement>("[data-currency-swap]");
  const host = container.querySelector<HTMLElement>("[data-currency-result-host]");

  const refresh = async () => {
    if (!amount || !from || !to || !host) return;
    const rawAmount = amount.value.trim().replace(/,/g, "");
    if (!rawAmount || !Number.isFinite(Number(rawAmount))) return;

    host.classList.add("is-loading");
    try {
      const next = await convertCurrency(rawAmount, from.value, to.value);
      host.innerHTML = resultMarkup(next);
    } catch (error) {
      console.error("Currency conversion failed:", error);
      host.innerHTML = `<div class="currency-card-error">Unable to refresh this conversion right now.</div>`;
    } finally {
      host.classList.remove("is-loading");
    }
  };

  amount?.addEventListener("change", refresh);
  from?.addEventListener("change", refresh);
  to?.addEventListener("change", refresh);
  swap?.addEventListener("click", () => {
    if (!from || !to) return;
    const previous = from.value;
    from.value = to.value;
    to.value = previous;
    void refresh();
  });
}

function converterCardMarkup(
  data: CurrencyResult,
  list: SupportedCurrency[],
): string {
  return `
    <section class="currency-converter-card" aria-label="Currency converter">
      <div class="currency-card-kicker">CURRENCY CONVERTER</div>
      <div class="currency-converter-grid">
        <label>
          <span>Amount</span>
          <input
            data-currency-amount
            inputmode="decimal"
            value="${esc(data.amount)}"
            aria-label="Currency amount"
          />
        </label>

        <label>
          <span>From</span>
          <select data-currency-from aria-label="From currency">
            ${options(list, data.from.code)}
          </select>
        </label>

        <button class="currency-swap-button" data-currency-swap type="button" aria-label="Swap currencies">⇄</button>

        <label>
          <span>To</span>
          <select data-currency-to aria-label="To currency">
            ${options(list, data.to.code)}
          </select>
        </label>
      </div>

      <div data-currency-result-host>
        ${resultMarkup(data)}
      </div>
    </section>
  `;
}

/**
 * Mount the existing converter design from an already-grounded backend payload.
 * Used by AI Overview so embedding never causes a second resolve request.
 */
export async function mountCurrencyConverterData(
  container: HTMLElement,
  data: CurrencyResult,
): Promise<void> {
  const list = await currencyListFor(data);
  container.innerHTML = converterCardMarkup(data, list);
  attachCurrencyConverterEvents(container);
}

export async function renderCurrencyConverter(
  container: HTMLElement,
  query: string,
): Promise<boolean> {
  container.innerHTML = `
    <section class="currency-converter-card currency-card-loading">
      Loading latest currency rate…
    </section>
  `;

  try {
    const data = await resolveCurrencyQuery(query);

    await mountCurrencyConverterData(container, data);

    return true;
  } catch (error) {
    console.error("AyeSearch currency resolver failed:", error);
    container.innerHTML = "";
    return false;
  }
}
